/**
 * Wallpaper roll calculator (Broadcast).
 * PDP: listens to theme.PUB_SUB_EVENTS.variantChange from product-info.js.
 * Standalone: sections with [data-wallpaper-calculator-standalone] (no product / cart).
 */
(function () {
  'use strict';

  function handleize(str) {
    return String(str || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-$/, '')
      .replace(/^-/, '');
  }

  function getNumber(num) {
    const s = String(num ?? '').trim();
    if (s === '' || Number.isNaN(Number(s))) return 0;
    return Number(s);
  }

  function variantDisplayLower(item) {
    return String(item.name || item.title || '').toLowerCase();
  }

  /** Same material detection order as legacy calculator.js (name/title string). */
  function materialSlugFromVariantItem(item) {
    const t = variantDisplayLower(item);
    if (t.includes('traditional')) return 'traditional';
    if (t.includes('peel')) return 'peel';
    if (t.includes('pre-pasted') || t.includes('prepasted')) return 'prepasted';
    return null;
  }

  function getRollBySample(sample, allVariants) {
    return allVariants.filter(function (item) {
      return (
        sample.option1 === item.option1 &&
        sample.option2 === item.option2 &&
        item.title.toLowerCase().includes('roll')
      );
    });
  }

  function resolveNonSampleVariant(variant, allVariants) {
    if (!variant || !variant.id) return null;
    if (!variant.title.toLowerCase().includes('sample')) return variant;
    const rolls = getRollBySample(variant, allVariants);
    return rolls.length ? rolls[0] : null;
  }

  function Calculator(fields, ctx) {
    if (!(this instanceof Calculator)) return new Calculator(fields, ctx);
    if (fields == null) throw new Error('Calculator requires a config object');
    this.fields = fields;
    this.ctx = ctx;
  }

  Calculator.prototype.validate = function () {
    const ctx = this.ctx;
    const errors = [];
    const fields = this.fields;
    const values = {};
    const el = ctx.el;

    if (fields.RoomHeightFeet === '' || fields.RoomHeightFeet == null || Number.isNaN(Number(fields.RoomHeightFeet))) {
      ctx.setError(el('HeightError'), 'Enter two valid numbers');
      errors.push('error');
    } else {
      ctx.setError(el('HeightError'), '');
      values.height = {
        feet: fields.RoomHeightFeet,
        inches: getNumber(fields.RoomHeightInches),
      };
    }

    const wallChecks = [
      { cls: 'js-width-one', feet: 'RoomWidthFeetOne', inches: 'RoomWidthInchesOne', err: 'WallErrorOne', key: 'wallOne' },
      { cls: 'js-width-two', feet: 'RoomWidthFeetTwo', inches: 'RoomWidthInchesTwo', err: 'WallErrorTwo', key: 'wallTwo' },
      { cls: 'js-width-three', feet: 'RoomWidthFeetThree', inches: 'RoomWidthInchesThree', err: 'WallErrorThree', key: 'wallThree' },
      { cls: 'js-width-four', feet: 'RoomWidthFeetFour', inches: 'RoomWidthInchesFour', err: 'WallErrorFour', key: 'wallFour' },
    ];

    wallChecks.forEach(function (w) {
      const group = ctx.form.querySelector('.' + w.cls);
      if (!group || !group.classList.contains('is-visible')) return;

      const feetVal = fields[w.feet];
      if (feetVal === '' || feetVal == null || Number.isNaN(Number(feetVal))) {
        ctx.setError(el(w.err), 'Enter two valid numbers');
        errors.push('error');
      } else {
        ctx.setError(el(w.err), '');
        values[w.key] = {
          feet: feetVal,
          inches: getNumber(fields[w.inches]),
        };
      }
    });

    if (errors.length) return false;
    return values;
  };

  Calculator.prototype.calculate = function (values) {
    const ctx = this.ctx;
    const form = ctx.form;
    const heightInches =
      parseFloat(12 * parseFloat(values.height.feet)) + parseFloat(values.height.inches);

    let widthInches = 0;
    ['wallOne', 'wallTwo', 'wallThree', 'wallFour'].forEach(function (k) {
      if (!values[k]) return;
      widthInches += parseFloat(12 * parseFloat(values[k].feet)) + parseFloat(values[k].inches);
    });

    const selected = form.querySelector('input[name="wallpaper-type"]:checked');
    if (!selected) return;

    const wallpaperMeasurement = parseFloat(selected.value);
    const dividedWidth = widthInches / wallpaperMeasurement;

    let heightBasedDiff = 1;
    if (selected.dataset.materialSlug === 'peel') {
      if (heightInches >= 1 && heightInches <= 35) heightBasedDiff = 6;
      else if (heightInches >= 36 && heightInches <= 71) heightBasedDiff = 3;
      else if (heightInches >= 72 && heightInches <= 107) heightBasedDiff = 2;
      else if (heightInches >= 108 && heightInches <= 214) heightBasedDiff = 1;
    } else {
      if (heightInches >= 1 && heightInches <= 35) heightBasedDiff = 10;
      else if (heightInches >= 36 && heightInches <= 71) heightBasedDiff = 5;
      else if (heightInches >= 72 && heightInches <= 107) heightBasedDiff = 3;
      else if (heightInches >= 108 && heightInches <= 179) heightBasedDiff = 2;
      else if (heightInches >= 180 && heightInches <= 360) heightBasedDiff = 1;
    }

    const result = Math.ceil(dividedWidth / heightBasedDiff);
    ctx.rollsEl.textContent = String(result);
    if (ctx.addBtn) ctx.addBtn.disabled = false;
  };

  function initWallpaperCalculatorStandalone(root, blockId) {
    const form = root.querySelector('[data-calculator-form]');
    if (!form) return;

    const el = function (suffix) {
      return root.querySelector('#wc-' + suffix + '-' + blockId);
    };

    const rollsEl = el('RollsNeeded');
    if (!rollsEl) return;

    const wallpaperInputs = function () {
      return form.querySelectorAll('input[name="wallpaper-type"]');
    };

    function setError(errorEl, message) {
      if (!errorEl) return;
      if (message) {
        errorEl.textContent = message;
        errorEl.hidden = false;
      } else {
        errorEl.textContent = '';
        errorEl.hidden = true;
      }
    }

    function syncStandaloneWallpaperTypes() {
      const typesSection = root.querySelector('.wallpaper-calculator__section--types');
      let firstInput = null;

      root.querySelectorAll('.js-calc__options__item').forEach(function (row) {
        row.hidden = false;
        if (!firstInput) {
          firstInput = row.querySelector('input[name="wallpaper-type"]');
        }
      });

      wallpaperInputs().forEach(function (inp) {
        inp.removeAttribute('required');
      });

      if (typesSection) typesSection.hidden = false;

      let anyChecked = false;
      wallpaperInputs().forEach(function (inp) {
        if (inp.checked) anyChecked = true;
      });
      if (!anyChecked && firstInput) {
        firstInput.checked = true;
      }

      if (firstInput) firstInput.required = true;
    }

    syncStandaloneWallpaperTypes();

    const calcCtx = {
      form: form,
      el: el,
      setError: setError,
      rollsEl: rollsEl,
      addBtn: null,
    };

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      const fields = Object.fromEntries(new FormData(form).entries());
      const calc = new Calculator(fields, calcCtx);
      const values = calc.validate();
      if (values) calc.calculate(values);
    });

    root.querySelectorAll('.js-add-wall').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        const group = btn.closest('.js-calculator-input-group');
        const next = group && group.nextElementSibling;
        if (!next || !next.classList.contains('js-calculator-input-group')) return;

        btn.classList.add('is-hidden');
        const rm = group.querySelector('.js-remove-wall');
        if (rm) rm.classList.add('is-hidden');

        next.classList.add('is-visible');
      });
    });

    root.querySelectorAll('.js-remove-wall').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        const group = btn.closest('.js-calculator-input-group');
        const prev = group && group.previousElementSibling;
        if (!group || !prev) return;

        group.querySelectorAll('input').forEach(function (inp) {
          inp.value = '';
        });
        group.classList.remove('is-visible');

        const prevAdd = prev.querySelector('.js-add-wall');
        const prevRm = prev.querySelector('.js-remove-wall');
        if (prevAdd) prevAdd.classList.remove('is-hidden');
        if (prevRm) prevRm.classList.remove('is-hidden');
      });
    });

    wallpaperInputs().forEach(function (input) {
      input.addEventListener('change', function () {
        rollsEl.textContent = '0';
      });
    });
  }

  function initWallpaperCalculator(root) {
    const blockId = root.dataset.blockId;
    if (!blockId) return;

    if (root.hasAttribute('data-wallpaper-calculator-standalone')) {
      initWallpaperCalculatorStandalone(root, blockId);
      return;
    }

    const productInfo = root.closest('product-info');
    const sectionId = root.dataset.productSectionId;
    const form = root.querySelector('[data-calculator-form]');
    const variantsEl = root.querySelector('[data-wallpaper-calculator-variants]');

    if (!form || !variantsEl || !productInfo) return;

    let allVariants = [];
    try {
      allVariants = JSON.parse(variantsEl.textContent || '[]');
    } catch {
      return;
    }

    const el = function (suffix) {
      return root.querySelector('#wc-' + suffix + '-' + blockId);
    };

    const rollsEl = el('RollsNeeded');
    const addBtn = el('VariantId');
    const variantTitleEl = el('calcVariantTitle');
    const wallpaperInputs = function () {
      return form.querySelectorAll('input[name="wallpaper-type"]');
    };

    function setError(errorEl, message) {
      if (!errorEl) return;
      if (message) {
        errorEl.textContent = message;
        errorEl.hidden = false;
      } else {
        errorEl.textContent = '';
        errorEl.hidden = true;
      }
    }

    const calcCtx = {
      form: form,
      el: el,
      setError: setError,
      rollsEl: rollsEl,
      addBtn: addBtn,
    };

    function syncWallpaperTypeRequiredAndSection() {
      const section = root.querySelector('.wallpaper-calculator__section--types');
      const rows = root.querySelectorAll('.js-calc__options__item');
      let anyVisible = false;
      let firstVisibleInput = null;

      rows.forEach(function (row) {
        if (row.hidden) return;
        anyVisible = true;
        if (!firstVisibleInput) {
          firstVisibleInput = row.querySelector('input[name="wallpaper-type"]');
        }
      });

      wallpaperInputs().forEach(function (inp) {
        inp.removeAttribute('required');
      });

      if (!anyVisible) {
        wallpaperInputs().forEach(function (inp) {
          inp.checked = false;
        });
      }

      if (section) {
        section.hidden = !anyVisible;
      }

      if (anyVisible && firstVisibleInput) {
        firstVisibleInput.required = true;
      }
    }

    function updateMaterialMeasurement(variant, variants) {
      if (!variant) return;

      const variantColor = variant.option1;
      const variantTitle = variant.title;

      root.querySelectorAll('.js-calc__options__item').forEach(function (row) {
        row.hidden = true;
      });

      let matchedCurrentTitle = false;

      variants.forEach(function (item) {
        if (item.option1 !== variantColor || item.title.toLowerCase().includes('sample')) return;

        const slug = materialSlugFromVariantItem(item);
        if (!slug) return;

        const row = root.querySelector('.js-calc__options__item[data-calc-option-row="' + slug + '"]');
        if (!row) return;

        row.hidden = false;

        if (item.title === variantTitle) {
          const input = row.querySelector('input[name="wallpaper-type"]');
          if (input) {
            input.checked = true;
            matchedCurrentTitle = true;
          }
        }
      });

      if (!matchedCurrentTitle) {
        const firstRow = root.querySelector('.js-calc__options__item:not([hidden])');
        if (firstRow) {
          const input = firstRow.querySelector('input[name="wallpaper-type"]');
          if (input) input.checked = true;
        }
      }

      syncWallpaperTypeRequiredAndSection();
    }

    function updateModalSKU(variant) {
      if (!variant) return;
      addBtn.dataset.variant = String(variant.id);
      variantTitleEl.textContent = variant.title;
      rollsEl.textContent = '0';
      addBtn.disabled = true;
    }

    function readSelectedVariantFromDom() {
      const script = productInfo.querySelector('variant-selects [data-selected-variant]');
      if (!script) return null;
      try {
        return JSON.parse(script.textContent);
      } catch {
        return null;
      }
    }

    function syncFromVariant(variant) {
      if (!variant || !variant.id) return;
      let v = resolveNonSampleVariant(variant, allVariants);
      if (!v) return;
      updateMaterialMeasurement(v, allVariants);
      updateModalSKU(v);
    }

    function onVariantChange(payload) {
      if (!payload || !payload.data) return;
      if (String(payload.data.sectionId) !== String(sectionId)) return;
      const variant = payload.data.variant;
      syncFromVariant(variant);
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      const fields = Object.fromEntries(new FormData(form).entries());
      const calc = new Calculator(fields, calcCtx);
      const values = calc.validate();
      if (values) calc.calculate(values);
    });

    root.querySelectorAll('.js-add-wall').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        const group = btn.closest('.js-calculator-input-group');
        const next = group && group.nextElementSibling;
        if (!next || !next.classList.contains('js-calculator-input-group')) return;

        btn.classList.add('is-hidden');
        const rm = group.querySelector('.js-remove-wall');
        if (rm) rm.classList.add('is-hidden');

        next.classList.add('is-visible');
      });
    });

    root.querySelectorAll('.js-remove-wall').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        const group = btn.closest('.js-calculator-input-group');
        const prev = group && group.previousElementSibling;
        if (!group || !prev) return;

        group.querySelectorAll('input').forEach(function (inp) {
          inp.value = '';
        });
        group.classList.remove('is-visible');

        const prevAdd = prev.querySelector('.js-add-wall');
        const prevRm = prev.querySelector('.js-remove-wall');
        if (prevAdd) prevAdd.classList.remove('is-hidden');
        if (prevRm) prevRm.classList.remove('is-hidden');
      });
    });

    wallpaperInputs().forEach(function (input) {
      input.addEventListener('change', function () {
        rollsEl.textContent = '0';
        addBtn.disabled = true;

        const materialSlug = input.dataset.materialSlug;
        if (!materialSlug) return;

        const currentId = parseInt(addBtn.dataset.variant, 10);
        const currentVariant = allVariants.find(function (item) {
          return item.id === currentId;
        });
        if (!currentVariant) return;

        const color = currentVariant.option1;
        const match = allVariants.find(function (item) {
          if (item.option1 !== color) return false;
          const ht = handleize(item.title || '');
          if (ht.includes('sample')) return false;
          return ht.includes(materialSlug);
        });

        if (match) updateModalSKU(match);
      });
    });

    addBtn.addEventListener('click', function (e) {
      e.preventDefault();
      const variantId = parseInt(addBtn.dataset.variant, 10);
      const qty = parseInt(rollsEl.textContent, 10) || 0;

      if (!variantId || qty < 1) {
        window.alert('No rolls to add to the cart. Calculate first.');
        return;
      }

      const base = window.theme.routes.cart_add_url.replace(/\/$/, '');
      const url = base.endsWith('.js') ? base : base + '.js';

      addBtn.disabled = true;

      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: qty }],
        }),
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Add to cart failed');
          return res.json();
        })
        .then(function () {
          document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('theme:product:added', { bubbles: true }));
        })
        .catch(function () {
          window.alert('Could not add to cart. Try again.');
        })
        .finally(function () {
          addBtn.disabled = false;
        });
    });

    if (window.subscribe && window.theme && window.theme.PUB_SUB_EVENTS) {
      window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, onVariantChange);
    }

    const initial = readSelectedVariantFromDom();
    if (initial) syncFromVariant(initial);
  }

  function boot() {
    document.querySelectorAll('[data-wallpaper-calculator-root]').forEach(initWallpaperCalculator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
